import { ClassType, EntityType, Rights } from '@frontend/common';
import { HorizontalButtonGroup } from '@frontend/components/common';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { ConfirmationModal } from '@frontend/components/modal';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { ModalType } from '@frontend/modal';
import { UserContactClient } from '@frontend/user/api';
import { IUser } from '@frontend/user/types';
import { FormattedMessage } from 'react-intl';

import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import OverviewHeader from '../../header/overview-header';
import { TabFilter } from '../../tab-filter/tab-filter.component';
import { CreateContactModal } from '../create-contact-modal/create-contact-modal.component';
import { useContacts } from './contacts.controller';

export const Contacts = () => {
    const viewProps = useContacts();
    return (
        <div
            key='contacts'
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={CommonMessage.OBJECTS.CONTACT.PLURAL}
                create={{
                    onCreate: () =>
                        viewProps.openModal('contacts-create-contact', () => <CreateContactModal />, {}, { type: ModalType.PANE, className: 'max-w-[600px]' }),
                    entity: CommonMessage.OBJECTS.CONTACT.DEFAULT,
                    overwriteCreateLabel: (
                        <FormattedMessage
                            id='feature.contacts.create-contact'
                            defaultMessage='Create contact'
                        />
                    )
                }}
                components={
                    <HorizontalButtonGroup
                        direction='right'
                        buttons={[
                            {
                                text: CommonMessage.BUTTONS.DELETE,
                                onClick: () =>
                                    viewProps.openModal('delete-contacts-modal', () => (
                                        <ConfirmationModal
                                            message={
                                                <FormattedMessage
                                                    id='ObjectList.multi-delete-modal.message'
                                                    defaultMessage='Are you sure you want to remove {count} {type}s?'
                                                    values={{ count: viewProps.selected?.length ?? 0, type: 'contact' }}
                                                />
                                            }
                                            severity={ClassType.DANGER}
                                            show={true}
                                            onConfirm={viewProps.onDelete}
                                            handleClose={viewProps.closeTopModal}
                                        />
                                    )),
                                hide: !viewProps.selected || viewProps.selected.length === 0,
                                requiredPermissions: [
                                    { item: EntityType.USER, source: EntityType.ACCOUNT, right: Rights.DELETE, uid: viewProps.currentAccount }
                                ],
                                className:
                                    'px-4 py-3 rounded-md text-sm bg-background-surface outline-offset-[-1px] outline-1 outline-status-danger hover:bg-status-danger hover:cursor-pointer hover:text-background-surface text-status-danger'
                            }
                        ]}
                    />
                }
            />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <TabFilter
                    keys={['personal_contacts', 'corporate_contacts']}
                    displayMap={
                        new Map([
                            [
                                'personal_contacts',
                                <FormattedMessage
                                    id='feature.contacts.tabs.personal_contacts'
                                    defaultMessage='Personal contacts'
                                    key='feature.contacts.tabs.personal_contacts'
                                />
                            ],
                            [
                                'corporate_contacts',
                                <FormattedMessage
                                    id='feature.contacts.tabs.corporate_contacts'
                                    defaultMessage='Corporate contacts'
                                    key='feature.contacts.tabs.corporate_contacts'
                                />
                            ]
                        ])
                    }
                    onChange={viewProps.setActiveTab}
                />
                <ContactList
                    key={viewProps.activeTab}
                    {...viewProps}
                />
            </div>
        </div>
    );
};

const ContactList = (viewProps: ReturnType<typeof useContacts>) => {
    const { openPlatformDetail } = usePlatformModalHost();
    const openContact = (id: string) =>
        openPlatformDetail({
            entityType: EntityType.USER,
            id,
            context: { application: Application.ADDRESS_BOOK, feature: Feature.CONTACTS }
        });

    switch (viewProps.activeTab) {
        case 'personal_contacts':
            return (
                <ReactiveObjectList<IUser>
                    id='personal-contacts-list'
                    useStaticListId
                    multiSelectOptions={{}}
                    fetch={(arg) => UserContactClient.getMyContacts(arg)}
                    dispatch={viewProps.dispatch}
                    activeFilter={{ searchKeys: ['first_name', 'last_name', 'email', 'mobile'] }}
                    cardClassName='card bg-always-white w-full border border-border-default'
                    columns={{
                        columns: ['first_name', 'last_name', 'email', 'mobile'],
                        headerOverwrite: new Map([
                            ['first_name', CommonMessage.OBJECTS.USER.FIRST_NAME],
                            ['last_name', CommonMessage.OBJECTS.USER.LAST_NAME],
                            ['email', CommonMessage.OBJECTS.USER.EMAIL],
                            ['mobile', CommonMessage.OBJECTS.USER.MOBILE]
                        ])
                    }}
                    type={EntityType.USER}
                    eventListener={EntityType.USER}
                    key={viewProps.activeTab}
                    onSelectElement={(obj) => openContact(obj.id)}
                />
            );
        case 'corporate_contacts':
            return (
                <ReactiveObjectList<IUser>
                    id='corporate-contacts-list'
                    useStaticListId
                    multiSelectOptions={{}}
                    fetch={(arg) => UserContactClient.getCorporateContacts(arg)}
                    dispatch={viewProps.dispatch}
                    activeFilter={{ searchKeys: ['unique_identifier', 'first_name', 'last_name', 'email', 'mobile'] }}
                    cardClassName='card bg-always-white w-full border border-border-default'
                    columns={{
                        columns: ['unique_identifier', 'first_name', 'last_name', 'email', 'mobile'],
                        headerOverwrite: new Map([
                            ['first_name', CommonMessage.OBJECTS.USER.FIRST_NAME],
                            ['last_name', CommonMessage.OBJECTS.USER.LAST_NAME],
                            ['email', CommonMessage.OBJECTS.USER.EMAIL],
                            ['mobile', CommonMessage.OBJECTS.USER.MOBILE]
                        ])
                    }}
                    type={EntityType.USER}
                    eventListener={EntityType.USER}
                    key={viewProps.activeTab}
                    onSelectElement={(obj) => openContact(obj.id)}
                />
            );
        default:
            return <div>{CommonMessage.OBJECTS.CONTACT.PLURAL}</div>;
    }
};
